
import React, { useState } from 'react';
import api from '../services/api';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const VideoUpload = () => {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [sizeCategory, setSizeCategory] = useState('desktop');
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && (selectedFile.type.startsWith('video/') || selectedFile.type.startsWith('image/'))) {
            setFile(selectedFile);
            setError(null);
        } else {
            setFile(null);
            setError('Please select a valid video or image file.');
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !title) {
            setError('Please provide a title and select a media file.');
            return;
        }

        const formData = new FormData();
        formData.append('video', file);
        formData.append('title', title);
        formData.append('subtitle', subtitle);
        formData.append('sizeCategory', sizeCategory);

        setUploading(true);
        setMessage(null);
        setError(null);

        try {
            // api service automatically handles baseURL and auth headers
            await api.post('/videos/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setMessage('Media uploaded successfully!');
            setTitle('');
            setSubtitle('');
            setFile(null);
            // Reset file input value somehow if needed, or controlled component not easy with file input
            document.getElementById('video-upload-input').value = '';
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to upload media.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Upload className="w-6 h-6 text-purple-600" />
                Upload Media
            </h2>

            {message && (
                <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4 flex items-center gap-2 border border-green-200">
                    <CheckCircle className="w-5 h-5" />
                    {message}
                </div>
            )}

            {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 flex items-center gap-2 border border-red-200">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            <form onSubmit={handleUpload} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Media Title
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                        placeholder="Enter media title"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subtitle / Description
                    </label>
                    <input
                        type="text"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                        placeholder="Enter subtitle or description (optional)"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Device Size
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                        {['mobile', 'desktop', 'pc'].map((size) => (
                            <label
                                key={size}
                                className={`
                  relative flex flex-col items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${sizeCategory === size
                                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                                        : 'border-gray-100 hover:border-gray-200 text-gray-600'}
                `}
                            >
                                <input
                                    type="radio"
                                    name="sizeCategory"
                                    value={size}
                                    checked={sizeCategory === size}
                                    onChange={(e) => setSizeCategory(e.target.value)}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <span className="capitalize font-medium">{size}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Media File
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors relative">
                        <input
                            id="video-upload-input"
                            type="file"
                            accept="video/*,image/*"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        {file ? (
                            <div className="flex items-center justify-center gap-2 text-purple-600 font-medium">
                                <CheckCircle className="w-5 h-5" />
                                {file.name}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                                <p className="text-gray-500">Click or drag media to upload</p>
                                <p className="text-xs text-gray-400">MP4, MOV, AVI, JPG, PNG (Max 50MB)</p>
                            </div>
                        )}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={uploading}
                    className={`
            w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all
            ${uploading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg hover:opacity-95'}
          `}
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <Upload className="w-5 h-5" />
                            Upload Media
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default VideoUpload;
